import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSimilarItineraries, getItinerary, archiveItinerary, unarchiveItinerary, softDeleteItinerary, restoreItinerary } from '../services/plannerApi';
import { ItineraryListItem } from '../types';
import UndoToast from '../components/common/UndoToast';
import { BottomNav } from '../components/navigation';
import { ArrowLeft } from 'lucide-react';
import { restoreNotice } from '../utils/publishOutcome';
import './SimilarPlansView.css';

const SimilarPlansView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<ItineraryListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [destination, setDestination] = useState('');
  const [toast, setToast] = useState<{ message: string; undoFn: () => void } | null>(null);
  // The restore notice (private provenance, decision 10), cleared on the next
  // delete, restore or route change. A monotonic generation guards each
  // completion, as in PlanTab: an older response never overwrites a newer
  // operation's notice.
  const [restoreNoticeText, setRestoreNoticeText] = useState<string | null>(null);
  const noticeGeneration = useRef(0);
  // React Router keeps this component mounted when only `:id` changes, so a
  // continuation started on one destination can finish on another. The route
  // generation is monotonic too (an equality check on `id` cannot tell
  // 5 → 6 → 5 apart), and a continuation reloads only on the route it began on:
  // its captured `load` is the OLD destination's.
  const routeGeneration = useRef(0);
  const startNoticeOp = () => {
    noticeGeneration.current += 1;
    setRestoreNoticeText(null);
    return noticeGeneration.current;
  };

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [itinerary, similar] = await Promise.all([
        getItinerary(Number(id)),
        getSimilarItineraries(Number(id)),
      ]);
      setDestination(itinerary.destination || itinerary.title);
      setPlans(similar);
    } catch {
      /* silent */
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    routeGeneration.current += 1;
    noticeGeneration.current += 1;
    setRestoreNoticeText(null);
    // A toast belongs to the destination it was raised on (r855).
    setToast(null);
  }, [id]);

  const handleArchive = async (plan: ItineraryListItem) => {
    setPlans(prev => prev.filter(p => p.id !== plan.id));
    try {
      await archiveItinerary(plan.id);
      setToast({
        message: `"${plan.title}" archived.`,
        undoFn: async () => {
          await unarchiveItinerary(plan.id);
          load();
        },
      });
    } catch {
      load();
    }
  };

  const handleDelete = async (plan: ItineraryListItem) => {
    setPlans(prev => prev.filter(p => p.id !== plan.id));
    startNoticeOp();
    const deleteRoute = routeGeneration.current;
    try {
      await softDeleteItinerary(plan.id);
      // A delete that answers after the route changed raises no toast here.
      if (routeGeneration.current !== deleteRoute) return;
      setToast({
        message: `"${plan.title}" deleted.`,
        undoFn: async () => {
          if (routeGeneration.current !== deleteRoute) {
            // Reached from a destination the page has left: the user still
            // asked for this plan back, so restore it, but write no notice
            // and reload nothing — neither belongs to the page now showing.
            await restoreItinerary(plan.id);
            return;
          }
          const op = startNoticeOp();
          const restored = await restoreItinerary(plan.id);
          if (noticeGeneration.current === op) setRestoreNoticeText(restoreNotice(restored?.publish));
          // `load` here is the closure of the render that deleted the plan, so
          // it reloads only while that render's route is still current.
          if (routeGeneration.current === deleteRoute) load();
        },
      });
    } catch {
      if (routeGeneration.current === deleteRoute) load();
    }
  };

  return (
    <div className="similar-plans-page">
      <div className="similar-plans-header">
        <button className="similar-plans-back" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <div className="similar-plans-header-text">
          <h1 className="similar-plans-heading">Your {destination} plans</h1>
          <p className="similar-plans-subtitle">
            {plans.length} plan{plans.length !== 1 ? 's' : ''} &middot; pick a canonical version, archive or delete the rest
          </p>
        </div>
      </div>

      <div className="similar-plans-content">
        {restoreNoticeText && (
          <p role="status" className="similar-plans-restore-notice">{restoreNoticeText}</p>
        )}
        {isLoading ? (
          <div className="similar-plans-loading">Loading...</div>
        ) : plans.length === 0 ? (
          <div className="similar-plans-empty">No similar plans found.</div>
        ) : (
          <div className="similar-plans-list">
            {plans.map(plan => {
              const dur = plan.start_date && plan.end_date
                ? Math.round((new Date(plan.end_date + 'T00:00:00').getTime() - new Date(plan.start_date + 'T00:00:00').getTime()) / 86400000) + 1
                : null;
              return (
                <div key={plan.id} className="similar-plan-card">
                  <div className="similar-plan-accent" />
                  <div className="similar-plan-body">
                    <div className="similar-plan-top">
                      <div className="similar-plan-info">
                        <span className="similar-plan-title">{plan.title}</span>
                        <span className="similar-plan-meta">
                          {dur && <>{dur} day{dur !== 1 ? 's' : ''} &middot; </>}
                          {plan.item_count} activit{plan.item_count !== 1 ? 'ies' : 'y'}
                        </span>
                      </div>
                      {plan.lifecycle_state && (
                        <span className={`similar-plan-badge similar-plan-badge--${plan.lifecycle_state}`}>
                          {plan.lifecycle_state.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="similar-plan-actions">
                      <button className="similar-plan-action similar-plan-action--open" onClick={() => navigate(`/itinerary/${plan.id}`)}>
                        Open
                      </button>
                      <button className="similar-plan-action similar-plan-action--archive" onClick={() => handleArchive(plan)}>
                        Archive
                      </button>
                      <button className="similar-plan-action similar-plan-action--delete" onClick={() => handleDelete(plan)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <UndoToast
          message={toast.message}
          onUndo={() => { toast.undoFn(); setToast(null); }}
          onDismiss={() => setToast(null)}
        />
      )}

      <BottomNav activeItem="plan" />
    </div>
  );
};

export default SimilarPlansView;
